import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Map } from 'immutable';
import classNames from 'classnames';
import moment from 'moment';

import { unwrap } from 'src/utils/ChangeSpy';
import { dayToString } from 'src/utils/dateFormat';
import Input from 'src/components/helpers/Input';
import FormGroup from 'src/components/helpers/FormGroup';
import Textarea from 'src/components/helpers/Textarea';
import { InputProps } from 'src/components/helpers/AutoComplete';
import { IconButton } from 'src/components/helpers/IconButton';
import AutoCompleteContainer from 'src/containers/AutoComplete';

import { FilesPanel } from './FilesPanel';
import { AgreementCategories } from './AgreementCategories';

import style from './style.module.css';

/**
 * Component is used to one-off type agreements list.
 *
 * @param {object} props Props.
 * @param {boolean} props.isReadOnly Whether all fields will be read-only or not.
 * @param {Function} props.onChange Handler for changing retainers controls.
 * @param {Function} props.onDelete Handler for deleting retainer from list.
 * @param {Function} props.onFindTargets Handler for find targets by search value.
 * @param {Function} props.onSuggestsChange Handler for changing targets suggests.
 * @param {Immutable.Map} props.root Company retainer.
 */
const OneOffAgreementsComponent = ({
  files,
  isReadOnly,
  root,
  onChange,
  onDelete,
  onFindTargets,
  onSuggestionsChange,
  onSelectAgreementCategories,
  onAgreementLink,
  onAgreementUnlink,
  onUploadFile,
  onDeleteFile,
}) => {
  const NAME_PREFIX = 'companyRetainers';
  const retainers = root.get(NAME_PREFIX).filter(retainer => unwrap(retainer.get('deleteType')) === undefined);

  return retainers.map((retainer, index) => {
    const id = retainer.get('id');
    const isSourceForAgreement = retainer.get('isSourceForAgreement');
    const isNew = typeof id === 'string';
    const agreementCategories = unwrap(retainer.get('agreementCategories'));
    const startDate = unwrap(retainer.get('startDate'));
    const dateTitle = moment.isMoment(startDate) && startDate.isValid() ? `- ${dayToString(startDate)}` : null;
    const agreementFiles = files.filter(file => file.get('companyRetainerId') === id);
    const formClassName = classNames(style.item, {
      [style.newAgreement]: isNew,
    });

    if (unwrap(retainer.get('type') === 'one-off')) {
      return (
        <div key={id} className={formClassName} id={`agreementId_${id}`}>
          <div className={style.titleWrap}>
            <h5 className={style.title}>One-Off Agreement {dateTitle}</h5>
          </div>
          <IconButton
            className={style.deleteButton}
            disabled={isReadOnly}
            icon="remove"
            onClick={() => onDelete(id, isSourceForAgreement)}
            size="sm"
            title="Delete"
            variant="danger"
          />
          <div className="row">
            <div className="col-xs-12 col-sm-5 col-md-5">
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm one-off-contractual-input"
                      disabled={isReadOnly}
                      label="Contractual Name"
                      name={`${NAME_PREFIX}.${index}.contractualName`}
                      onChange={onChange}
                      value={retainer.get('contractualName')}
                      showLabel
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <FormGroup>
                    <label>Target</label>
                    <InputProps className="form-control input-sm" placeholder="Target">
                      <AutoCompleteContainer
                        idName={`${NAME_PREFIX}.${index}.targetId`}
                        idValue={root.getIn([`${NAME_PREFIX}`, index, 'targetId'])}
                        onChange={onSuggestionsChange}
                        onFind={onFindTargets}
                        suggestions={root.get('targetsSuggests')}
                        suggestionsName="targetsSuggests"
                        value={root.getIn([`${NAME_PREFIX}`, index, 'targetName'])}
                        valueName={`${NAME_PREFIX}.${index}.targetName`}
                      />
                    </InputProps>
                  </FormGroup>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm"
                      defaultValue={null}
                      disabled={isReadOnly}
                      label="Start Date"
                      name={`${NAME_PREFIX}.${index}.startDate`}
                      onChange={onChange}
                      placeholder="Start Date"
                      title="This is the date, per the contract, that the project kicks off"
                      type="date"
                      value={retainer.get('startDate')}
                      showLabel
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6">
                  <div className="form-group">
                    <Input
                      className="input-sm"
                      defaultValue={null}
                      disabled={isReadOnly}
                      label="End Date"
                      name={`${NAME_PREFIX}.${index}.endDate`}
                      onChange={onChange}
                      placeholder="End Date"
                      title="This is the date the agreement officially terminates"
                      type="date"
                      value={retainer.get('endDate')}
                      showLabel
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <Textarea
                    disabled={isReadOnly}
                    label="Notes"
                    name={`${NAME_PREFIX}.${index}.retainerNote`}
                    onChange={onChange}
                    placeholder=""
                    rows="4"
                    value={retainer.get('retainerNote')}
                    showLabel
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 mt10">
                  {isNew ? (
                    <b>Please save the changes before uploading the files.</b>
                  ) : (
                    <FilesPanel
                      files={agreementFiles}
                      onDeleteFile={onDeleteFile}
                      onUploadFile={event => onUploadFile(event, id)}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-7 col-md-7">
              <AgreementCategories
                agreementCategories={agreementCategories}
                onAgreementLink={selectedAgreementCategory =>
                  onAgreementLink({ agreementCategories, type: 'companyRetainers', index, selectedAgreementCategory })
                }
                onAgreementUnlink={agreementCategories =>
                  onAgreementUnlink({ agreementCategories, type: 'companyRetainers', index })
                }
                onSelectAgreementCategories={() =>
                  onSelectAgreementCategories(agreementCategories, 'companyRetainers', index)
                }
              />
            </div>
          </div>
        </div>
      );
    }

    return null;
  });
};

OneOffAgreementsComponent.propTypes = {
  isReadOnly: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFindTargets: PropTypes.func.isRequired,
  onSuggestionsChange: PropTypes.func.isRequired,
  root: PropTypes.instanceOf(Map).isRequired,
};

/** One off agreements component. */
export const OneOffAgreements = memo(OneOffAgreementsComponent);
